import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export const STEP_ORDER = ['SHIPMENT', 'SHIPPER', 'RECEIVER', 'SERVICE'] as const;

export type StepKey = (typeof STEP_ORDER)[number];

type AccordionState = {
    activeStep: StepKey;
    completedSteps: Partial<Record<StepKey, boolean>>;
};

const initialState: AccordionState = {
    activeStep: 'SHIPMENT',
    completedSteps: {},
};

const activeAccordionSlice = createSlice({
    name: 'activeAccordion',
    initialState,
    reducers: {
        setActiveAccordion: (state, action: PayloadAction<StepKey>) => {
            const target = action.payload;

            const currentIndex = STEP_ORDER.indexOf(state.activeStep);
            const targetIndex = STEP_ORDER.indexOf(target);

            // Allow navigation to:
            // 1. Current step or previous step (backward)
            // 2. Next step only if current is completed
            // 3. Any completed step (but this will invalidate steps after it)

            if (targetIndex < currentIndex) {
                // Going backward - invalidate all steps after the target
                state.activeStep = target;
                for (let i = targetIndex + 1; i < STEP_ORDER.length; i++) {
                    delete state.completedSteps[STEP_ORDER[i]];
                }
            } else if (targetIndex === currentIndex) {
                // Staying on current step
                state.activeStep = target;
            } else if (targetIndex === currentIndex + 1 && state.completedSteps[state.activeStep]) {
                // Going forward to next step only if current is completed
                state.activeStep = target;
            }
            // Else: trying to jump ahead - do nothing
        },

        markStepcompleted: (state, action: PayloadAction<StepKey>) => {
            const step = action.payload;
            state.completedSteps[step] = true;

            const stepIndex = STEP_ORDER.indexOf(step);
            const currentIndex = STEP_ORDER.indexOf(state.activeStep);

            if (stepIndex === currentIndex && stepIndex < STEP_ORDER.length - 1) {
                state.activeStep = STEP_ORDER[stepIndex + 1];
            }
        },

        resetAccordion(state) {
            state.activeStep = 'SHIPMENT';
            state.completedSteps = {};
        },
    },
});

export const { setActiveAccordion, markStepcompleted, resetAccordion } =
    activeAccordionSlice.actions;
export default activeAccordionSlice.reducer;
